import { DeckFlavor } from '@/enums/Language'
import type {
	PartialSessionData,
	SessionAnalyticsData,
	SessionData,
} from '@/types'

export class Session {
	public lastUpdated: string | undefined = undefined
	public planIds: string[] = []
	public currentSlideIndex = 0
	public readonly appointmentId: string | null = null
	public readonly salesforceEventAppointmentId: string | null = null
	public readonly id: string
	public language: DeckFlavor = DeckFlavor.Default
	public startAt: string | null = null
	public resultedAt: string | null = null

	public slideDeckId = ''
	public FHGroupMediaId = ''
	public crmFhGroupId = undefined
	public FuneralHomeLocationId: string | undefined
	public analytics: SessionAnalyticsData | undefined

	constructor(sessionData: PartialSessionData) {
		// Unpack Data
		this.id = sessionData.id ?? crypto.randomUUID()
		this.lastUpdated = sessionData.lastUpdated
			? new Date(sessionData.lastUpdated).toISOString()
			: new Date().toISOString()
		this.planIds = sessionData.planIds ?? []
		this.currentSlideIndex = sessionData.currentSlideIndex ?? 0
		this.appointmentId = sessionData.appointmentId ?? null
		this.salesforceEventAppointmentId =
			sessionData.salesforceEventAppointmentId ?? null
		this.language = sessionData.language ?? DeckFlavor.Default
		this.FuneralHomeLocationId = sessionData.FuneralHomeLocationId
		this.startAt = sessionData.startAt ?? null
		this.resultedAt = sessionData.resultedAt ?? null

		if (sessionData.slideDeckId) {
			this.slideDeckId = sessionData.slideDeckId
		}

		if (sessionData.FHGroupMediaId) {
			this.FHGroupMediaId = sessionData.FHGroupMediaId
		}

		if (sessionData.analytics) {
			this.analytics = sessionData.analytics
		}
	}

	/**
	 * Adds a new plan ID to the session's list of plan IDs.
	 * The actual plan must be created and managed separately through the plans store.
	 */
	public addPlanId(planId: string) {
		this.planIds.push(planId)
	}

	/**
	 * Removes a plan ID from the session.
	 */
	public removePlanId(planId: string) {
		this.planIds = this.planIds.filter((id) => id !== planId)
	}

	public serialize(): SessionData {
		return {
			id: this.id,
			lastUpdated: this.lastUpdated,
			appointmentId: this.appointmentId,
			salesforceEventAppointmentId: this.salesforceEventAppointmentId,
			planIds: this.planIds,
			currentSlideIndex: this.currentSlideIndex,
			slideDeckId: this.slideDeckId,
			FHGroupMediaId: this.FHGroupMediaId,
			FuneralHomeLocationId: this.FuneralHomeLocationId,
			language: this.language,
			startAt: this.startAt,
			resultedAt: this.resultedAt,
			analytics: this.analytics,
		}
	}

	public static deserialize(data: PartialSessionData) {
		const session = new Session(data)
		return session
	}
}
